import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FaFileInvoiceDollar, FaPhone, FaPlus, FaArrowLeft } from 'react-icons/fa6';
import { useSip } from '../../SipContext';
import axios from '../../../configApi/axiosConfig';
import { APIURL } from '../../../configApi/apiConfig';

import { useDetalleCobranzaData } from './hooks/useDetalleCobranzaData';
import { DashboardHeader } from './components/DashboardHeader';
import { TabNavigation } from './components/TabNavigation';
import { TabContent } from './components/TabContent';
import { SelectorTema, useTemaGestion } from './TemaGestion';

export function DetalleCobranza({ isOpen, onClose, data, estadoGestion, item, isPage = false }) {
    const { openDialer, phoneAvailable } = useSip();
    const [activeTab, setActiveTab] = useState('GESTION');
    const [selectedId, setSelectedId] = useState(null);
    const [oscuro, setOscuro] = useTemaGestion();

    const {
        loading,
        selectResultado,
        direccionData,
        datosCobranza,
        historialAPI,
        amortizacionStats,
        articulosFactura,
        estadoGestionData,
        refetch
    } = useDetalleCobranzaData(data, isOpen, estadoGestion);

    const handleGuardarTelefonos = async (telefonosGuardados) => {
        try {
            const userInfoString = localStorage.getItem('userInfo');
            const userInfo = userInfoString ? JSON.parse(userInfoString) : null;
            const usuario = userInfo?.Usuario || 'SISTEMA';
            const estacion = userInfo?.Estacion || 'WEB';
            let idCliente = data?.idCliente || item?.idCliente || null;
            const cedula = data?.Cedula || item?.Cedula || null;

            if (!idCliente && cedula) {
                try {
                    const res = await axios.get(APIURL.getCliente(cedula));
                    if (res.data?.success && res.data?.data?.idCliente) {
                        idCliente = res.data.data.idCliente;
                    }
                } catch (error) {
                    console.error("Error fetching idCliente:", error);
                }
            }

            const promesas = telefonosGuardados.map(async (t) => {
                if (t.idCre_GCTelefono && t.idCre_GCTelefono.toString().startsWith('TELF_') && 
                    !t.idCre_GCTelefono.toString().startsWith('TELF_INIT_') && 
                    !t.idCre_GCTelefono.toString().startsWith('TELF_DB_') &&
                    !t.idCre_GCTelefono.toString().startsWith('TELF_MAESTRO_')) {
                    const payload = {
                        idCliente: idCliente,
                        Telefono: t.telefono,
                        Descripcion: t.descripcion,
                        FechaSistema: new Date(),
                        Estacion: estacion,
                        Usuario: usuario
                    };
                    return axios.post(APIURL.post_creGcTelefono(), payload);
                }
                return Promise.resolve();
            });

            await Promise.all(promesas);
            alert('Teléfonos guardados exitosamente');
            setActiveTab('GESTION');
        } catch (error) {
            console.error('Error guardando teléfonos:', error);
            alert('Error al guardar los teléfonos');
        }
    };

    if (!isOpen) return null;

    const diasMora = Number(amortizacionStats?.fetched && amortizacionStats?.diasMora !== undefined
        ? amortizacionStats.diasMora
        : (data?.Dias_Mora_Proyectado || 0));
    const badgeMora = diasMora > 30
        ? { texto: 'Mora crítica', clase: 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/70 dark:text-rose-400 dark:border-rose-800/60', punto: 'bg-rose-500 animate-pulse' }
        : diasMora > 0
            ? { texto: 'En mora', clase: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/60', punto: 'bg-amber-500' }
            : { texto: 'Al día', clase: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/60', punto: 'bg-emerald-500' };

    return (
        // Tailwind darkMode 'class': todo el detalle cambia de tema con la clase .dark
        <div className={`${oscuro ? 'dark' : ''} ${isPage ? 'w-full h-full' : 'fixed inset-0 z-50 overflow-hidden'}`}>
            {/* Overlay */}
            {!isPage && <div className="absolute inset-0 bg-black/50 backdrop-blur-md transition-opacity" onClick={onClose}></div>}

            {/* Modal */}
            <div className={isPage ? "w-full mx-auto" : "absolute inset-0 overflow-hidden"}>
                <div className={isPage ? "flex items-start justify-center min-h-screen" : "flex items-center justify-center min-h-screen p-4"}>
                    <div className={`relative bg-slate-50 dark:bg-[#0B0F19] text-slate-900 dark:text-slate-100 shadow-2xl w-full flex flex-col ${isPage ? 'min-h-screen rounded-2xl overflow-hidden' : 'rounded-3xl max-w-7xl max-h-[95vh] overflow-hidden'}`}>

                        {/* Encabezado (mockup TopHeader) */}
                        <header className="bg-white dark:bg-[#111827] border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30 shadow-sm">
                            <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white shadow-sm ring-1 ring-indigo-400/30 shrink-0">
                                        <FaFileInvoiceDollar className="text-base" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h1 className="text-base font-bold tracking-tight text-slate-900 dark:text-white leading-tight">Historial de Gestiones</h1>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${badgeMora.clase}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${badgeMora.punto}`} /> {badgeMora.texto}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                            Expediente de recuperación de cartera • Crédito #{data?.idCompra ?? '-'}{data?.Numero_Documento ? ` • ${data.Numero_Documento}` : ''}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3">
                                    <SelectorTema oscuro={oscuro} onCambiar={setOscuro} />
                                    {phoneAvailable && (
                                        <div className="hidden xl:flex items-center bg-slate-100 dark:bg-[#1A2234] rounded-lg p-1 border border-slate-200 dark:border-slate-700/80">
                                            <button type="button" onClick={() => openDialer?.({ trackingId: data?.idCompra ?? null, clientName: data?.Cliente ?? '' })} title="Abrir el marcador"
                                                className="px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700/70 rounded transition flex items-center gap-1.5">
                                                <FaPhone className="text-emerald-500 dark:text-emerald-400 text-xs" />
                                                <span>Llamar</span>
                                            </button>
                                        </div>
                                    )}
                                    <button type="button" onClick={() => setActiveTab('SAVE_GESTION')}
                                        className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition">
                                        <FaPlus className="text-xs" />
                                        <span className="hidden sm:inline">Registrar Gestión</span>
                                        <span className="sm:hidden">Registrar</span>
                                    </button>
                                    <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1" />
                                    {isPage ? (
                                        <button type="button" onClick={() => window.close()} title="Cerrar y volver al listado"
                                            className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-[#1A2234] hover:bg-slate-200 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-slate-700 px-2.5 sm:px-3 py-1.5 rounded-lg transition">
                                            <FaArrowLeft className="text-xs" />
                                            <span className="hidden sm:inline">Volver</span>
                                        </button>
                                    ) : (
                                        <button type="button" onClick={onClose} title="Cerrar"
                                            className="p-1.5 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 rounded-lg transition">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </header>

                        {/* Premium Dashboard Cards */}
                        <DashboardHeader 
                            data={data} 
                            item={item} 
                            direccionData={direccionData} 
                            amortizacionStats={amortizacionStats}
                            articulosFactura={articulosFactura}
                        />

                        {/* Tabs Navigation */}
                        <TabNavigation
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            setSelectedId={setSelectedId}
                            data={data}
                            totalGestiones={historialAPI?.length}
                        />

                        {/* Content - Scrollable */}
                        <div className="overflow-y-auto flex-1 min-h-0 bg-slate-50 dark:bg-[#0B0F19] relative">
                            <div className="p-4 md:px-6 md:py-4">
                                <TabContent 
                                    activeTab={activeTab} 
                                    setActiveTab={setActiveTab} 
                                    selectedId={selectedId}
                                    data={data} 
                                    estadoGestion={estadoGestionData?.length > 0 ? estadoGestionData : estadoGestion} 
                                    historialAPI={historialAPI} 
                                    direccionData={direccionData} 
                                    loading={loading}
                                    handleGuardarTelefonos={handleGuardarTelefonos} 
                                    refetch={refetch}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
